/**
 * PlantasIotConfigDialog.jsx  v2
 * Configuración de Plantas IoT (requiere permiso PlantasIot / configuracion)
 * Pestañas: Plantas (alta, edición, activar, rotar key) · Tanques (nombre, capacidad, umbrales)
 * v2: pestaña Avisos (envío diario por SMS / mail, en PlantasIotAvisosTab)
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, IconButton, MenuItem, Paper, Select, Stack, Switch, Tab, Tabs,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Key as KeyIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import PlantasIotAvisosTab from './PlantasIotAvisosTab';

const ID_RE = /^[a-z0-9_-]{1,50}$/;

function fmtFecha(iso) {
    return iso ? new Date(iso).toLocaleString('es-AR', { hour12: false }) : '—';
}

const aTexto = (v) => (v === null || v === undefined ? '' : String(v));
const aValor = (s) => (String(s).trim() === '' ? null : Number(String(s).replace(',', '.')));

export default function PlantasIotConfigDialog({ open, apiUrl, onClose }) {
    const { enqueueSnackbar } = useSnackbar();

    const [tab, setTab] = useState(0);
    const [huboCambios, setHuboCambios] = useState(false);

    // Plantas
    const [plantas, setPlantas] = useState([]);
    const [loadingPl, setLoadingPl] = useState(false);
    const [errorPl, setErrorPl] = useState(null);
    const [nueva, setNueva] = useState({ id: '', nombre: '' });
    const [creando, setCreando] = useState(false);

    // Key generada (se muestra una sola vez)
    const [keyMostrada, setKeyMostrada] = useState(null);   // { plantaId, api_key, motivo }
    const [confirmarRotar, setConfirmarRotar] = useState(null); // plantaId

    // Tanques
    const [plantaTq, setPlantaTq] = useState('');
    const [tanques, setTanques] = useState([]);
    const [loadingTq, setLoadingTq] = useState(false);
    const [errorTq, setErrorTq] = useState(null);

    const apiAdmin = useCallback(async (path, method = 'GET', body) => {
        const r = await fetch(`${apiUrl}/api/plantas-iot/admin${path}`, {
            method,
            headers: {
                'x-api-key': process.env.REACT_APP_HMI_API_KEY || '',
                Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                ...(body ? { 'Content-Type': 'application/json' } : {}),
            },
            body: body ? JSON.stringify(body) : undefined,
        });
        let data = null;
        try { data = await r.json(); } catch { /* sin cuerpo */ }
        if (!r.ok) throw new Error((data && data.error) || `HTTP ${r.status}`);
        return data;
    }, [apiUrl]);

    // ── Plantas ────────────────────────────────────────────────────────────────
    const cargarPlantas = useCallback(async () => {
        setLoadingPl(true);
        setErrorPl(null);
        try {
            const ps = await apiAdmin('/plantas');
            setPlantas(ps.map((p) => ({ ...p, _dirty: false })));
            setPlantaTq((prev) => prev || (ps[0] ? ps[0].id : ''));
        } catch (err) {
            setErrorPl(err.message);
        } finally {
            setLoadingPl(false);
        }
    }, [apiAdmin]);

    useEffect(() => {
        if (!open) return;
        setTab(0);
        setHuboCambios(false);
        setNueva({ id: '', nombre: '' });
        cargarPlantas();
    }, [open, cargarPlantas]);

    const editarPlanta = (id, campo, valor) =>
        setPlantas((ps) => ps.map((p) => (p.id === id ? { ...p, [campo]: valor, _dirty: true } : p)));

    async function guardarPlanta(p) {
        try {
            await apiAdmin(`/plantas/${p.id}`, 'PUT', { nombre: p.nombre, activa: p.activa });
            setPlantas((ps) => ps.map((x) => (x.id === p.id ? { ...x, _dirty: false } : x)));
            setHuboCambios(true);
            enqueueSnackbar(`Planta "${p.nombre}" guardada`, { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    async function crearPlanta() {
        setCreando(true);
        try {
            const r = await apiAdmin('/plantas', 'POST', { id: nueva.id, nombre: nueva.nombre });
            setKeyMostrada({ plantaId: r.id, api_key: r.api_key, motivo: 'alta' });
            setNueva({ id: '', nombre: '' });
            setHuboCambios(true);
            cargarPlantas();
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        } finally {
            setCreando(false);
        }
    }

    async function rotarKey(plantaId) {
        setConfirmarRotar(null);
        try {
            const r = await apiAdmin(`/plantas/${plantaId}/rotar-key`, 'POST');
            setKeyMostrada({ plantaId: r.id, api_key: r.api_key, motivo: 'rotacion' });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    async function copiarKey() {
        try {
            await navigator.clipboard.writeText(keyMostrada.api_key);
            enqueueSnackbar('API key copiada', { variant: 'success' });
        } catch {
            enqueueSnackbar('No se pudo copiar, seleccionala a mano', { variant: 'warning' });
        }
    }

    const idNuevaValido = ID_RE.test(nueva.id);
    const puedeCrear = idNuevaValido && nueva.nombre.trim() !== '' && !creando;

    // ── Tanques ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open || tab !== 1 || !plantaTq) return;
        let activo = true;
        setLoadingTq(true);
        setErrorTq(null);
        apiAdmin(`/plantas/${plantaTq}/tanques`)
            .then((ts) => {
                if (!activo) return;
                setTanques(ts.map((t) => ({
                    ...t,
                    f_nombre: aTexto(t.nombre),
                    f_capacidad: aTexto(t.capacidad_m3),
                    f_baja: aTexto(t.alerta_baja_pct),
                    f_alta: aTexto(t.alerta_alta_pct),
                    _dirty: false,
                })));
            })
            .catch((err) => activo && setErrorTq(err.message))
            .finally(() => activo && setLoadingTq(false));
        return () => { activo = false; };
    }, [open, tab, plantaTq, apiAdmin]);

    const editarTanque = (tanqueId, campo, valor) =>
        setTanques((ts) => ts.map((t) => (t.tanque_id === tanqueId ? { ...t, [campo]: valor, _dirty: true } : t)));

    async function guardarTanque(t) {
        try {
            await apiAdmin(`/plantas/${plantaTq}/tanques/${t.tanque_id}`, 'PUT', {
                nombre: t.f_nombre.trim() || null,
                capacidad_m3: aValor(t.f_capacidad),
                alerta_baja_pct: aValor(t.f_baja),
                alerta_alta_pct: aValor(t.f_alta),
            });
            setTanques((ts) => ts.map((x) => (x.tanque_id === t.tanque_id ? { ...x, _dirty: false } : x)));
            setHuboCambios(true);
            enqueueSnackbar(`Tanque ${t.tanque || t.tanque_id} guardado`, { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    const celda = { py: 0.5, px: 1, fontSize: 12 };
    const inputSx = { '& .MuiInputBase-input': { fontSize: 12, py: 0.7 } };

    return (
        <>
            <Dialog open={open} onClose={() => onClose(huboCambios)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}>

                <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" fontWeight={700}>Configuración · Plantas IoT</Typography>
                        <IconButton size="small" onClick={() => onClose(huboCambios)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Tab label="Plantas" sx={{ fontSize: 12, minHeight: 42 }} />
                        <Tab label="Tanques" sx={{ fontSize: 12, minHeight: 42 }} disabled={plantas.length === 0} />
                        <Tab label="Avisos" sx={{ fontSize: 12, minHeight: 42 }} disabled={plantas.length === 0} />
                    </Tabs>

                    <Box sx={{ p: 2, overflowY: 'auto', maxHeight: '65vh' }}>

                        {/* ── TAB PLANTAS ───────────────────────────────────── */}
                        {tab === 0 && (
                            <>
                                {loadingPl ? (
                                    <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
                                ) : errorPl ? (
                                    <Alert severity="error">{errorPl}</Alert>
                                ) : (
                                    <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={celda}>Id</TableCell>
                                                    <TableCell sx={celda}>Nombre</TableCell>
                                                    <TableCell sx={celda} align="center">Activa</TableCell>
                                                    <TableCell sx={celda}>Último envío</TableCell>
                                                    <TableCell sx={celda} align="right">Acciones</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {plantas.map((p) => (
                                                    <TableRow key={p.id}>
                                                        <TableCell sx={{ ...celda, fontFamily: "'Roboto Mono', monospace" }}>{p.id}</TableCell>
                                                        <TableCell sx={celda}>
                                                            <TextField
                                                                size="small" value={p.nombre}
                                                                onChange={(e) => editarPlanta(p.id, 'nombre', e.target.value)}
                                                                sx={{ ...inputSx, minWidth: 180 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={celda} align="center">
                                                            <Tooltip title={p.activa ? 'Activa: recibe datos y se ve en el dashboard' : 'Inactiva: se rechazan sus envíos y no se muestra'}>
                                                                <Switch size="small" checked={p.activa}
                                                                    onChange={(e) => editarPlanta(p.id, 'activa', e.target.checked)} />
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ ...celda, whiteSpace: 'nowrap' }}>{fmtFecha(p.ultimo_envio)}</TableCell>
                                                        <TableCell sx={{ ...celda, whiteSpace: 'nowrap' }} align="right">
                                                            <Tooltip title="Guardar cambios">
                                                                <span>
                                                                    <IconButton size="small" color="primary" disabled={!p._dirty || !p.nombre.trim()}
                                                                        onClick={() => guardarPlanta(p)}>
                                                                        <SaveIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Generar API key nueva">
                                                                <IconButton size="small" color="warning" onClick={() => setConfirmarRotar(p.id)}>
                                                                    <KeyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Paper>
                                )}

                                {/* Alta de planta */}
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" color="text.disabled"
                                        sx={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                                        Nueva planta
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                                        <TextField
                                            size="small" label="Id" value={nueva.id}
                                            onChange={(e) => setNueva((n) => ({ ...n, id: e.target.value.toLowerCase().trim() }))}
                                            error={nueva.id !== '' && !idNuevaValido}
                                            helperText="Igual a planta.id del JSON del equipo (ej: corrientes)"
                                            sx={{ minWidth: 200 }}
                                        />
                                        <TextField
                                            size="small" label="Nombre" value={nueva.nombre}
                                            onChange={(e) => setNueva((n) => ({ ...n, nombre: e.target.value }))}
                                            sx={{ minWidth: 220 }}
                                        />
                                        <Button variant="contained" size="small" startIcon={<AddIcon />}
                                            disabled={!puedeCrear} onClick={crearPlanta} sx={{ mt: { sm: 0.4 }, whiteSpace: 'nowrap' }}>
                                            {creando ? 'Creando…' : 'Crear planta'}
                                        </Button>
                                    </Stack>
                                </Paper>
                            </>
                        )}

                        {/* ── TAB TANQUES ───────────────────────────────────── */}
                        {tab === 1 && (
                            <>
                                <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
                                    <Select value={plantaTq} onChange={(e) => setPlantaTq(e.target.value)} sx={{ fontSize: 13 }}>
                                        {plantas.map((p) => (
                                            <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>{p.nombre}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {loadingTq ? (
                                    <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
                                ) : errorTq ? (
                                    <Alert severity="error">{errorTq}</Alert>
                                ) : tanques.length === 0 ? (
                                    <Alert severity="info">Esta planta todavía no envió lecturas de tanques (últimos 30 días).</Alert>
                                ) : (
                                    <>
                                        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={celda}>Tanque</TableCell>
                                                        <TableCell sx={celda}>Nombre visible</TableCell>
                                                        <TableCell sx={celda}>Capacidad m³</TableCell>
                                                        <TableCell sx={celda}>Alerta baja %</TableCell>
                                                        <TableCell sx={celda}>Alerta alta %</TableCell>
                                                        <TableCell sx={celda} align="right"></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tanques.map((t) => (
                                                        <TableRow key={t.tanque_id}>
                                                            <TableCell sx={{ ...celda, whiteSpace: 'nowrap' }}>
                                                                <Typography fontSize={12} fontWeight={600}>{t.tanque || `#${t.tanque_id}`}</Typography>
                                                                <Typography fontSize={10} color="text.disabled" sx={{ textTransform: 'capitalize' }}>
                                                                    {t.producto || '—'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={celda}>
                                                                <TextField size="small" value={t.f_nombre} placeholder={t.tanque || ''}
                                                                    onChange={(e) => editarTanque(t.tanque_id, 'f_nombre', e.target.value)}
                                                                    sx={{ ...inputSx, minWidth: 140 }} />
                                                            </TableCell>
                                                            <TableCell sx={celda}>
                                                                <TextField size="small" value={t.f_capacidad} inputMode="decimal"
                                                                    onChange={(e) => editarTanque(t.tanque_id, 'f_capacidad', e.target.value)}
                                                                    sx={{ ...inputSx, width: 100 }} />
                                                            </TableCell>
                                                            <TableCell sx={celda}>
                                                                <TextField size="small" value={t.f_baja} inputMode="decimal"
                                                                    onChange={(e) => editarTanque(t.tanque_id, 'f_baja', e.target.value)}
                                                                    sx={{ ...inputSx, width: 80 }} />
                                                            </TableCell>
                                                            <TableCell sx={celda}>
                                                                <TextField size="small" value={t.f_alta} inputMode="decimal"
                                                                    onChange={(e) => editarTanque(t.tanque_id, 'f_alta', e.target.value)}
                                                                    sx={{ ...inputSx, width: 80 }} />
                                                            </TableCell>
                                                            <TableCell sx={celda} align="right">
                                                                <Tooltip title="Guardar">
                                                                    <span>
                                                                        <IconButton size="small" color="primary" disabled={!t._dirty}
                                                                            onClick={() => guardarTanque(t)}>
                                                                            <SaveIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Paper>
                                        <Typography variant="caption" color="text.disabled" display="block" mt={1} fontSize={10}>
                                            Los campos vacíos quedan sin valor. Si el nombre visible está vacío, se usa el que envía el equipo.
                                            Los umbrales marcan el tanque en pantalla; las alarmas registradas son las que envía el proveedor.
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}

                        {/* ── TAB AVISOS ────────────────────────────────────── */}
                        {tab === 2 && <PlantasIotAvisosTab plantas={plantas} apiAdmin={apiAdmin} />}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Confirmación de rotación */}
            <Dialog open={!!confirmarRotar} onClose={() => setConfirmarRotar(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>¿Generar una API key nueva?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        La key actual de <b>{confirmarRotar}</b> deja de funcionar en hasta un minuto.
                        El equipo de la planta no va a poder enviar datos hasta que el proveedor cargue la nueva.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmarRotar(null)}>Cancelar</Button>
                    <Button variant="contained" color="warning" onClick={() => rotarKey(confirmarRotar)}>Generar nueva</Button>
                </DialogActions>
            </Dialog>

            {/* Key generada — se muestra una sola vez */}
            <Dialog open={!!keyMostrada} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>
                    {keyMostrada?.motivo === 'alta' ? 'Planta creada' : 'API key nueva'} · {keyMostrada?.plantaId}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Copiala ahora: <b>no se puede volver a ver</b>, en la base solo queda el hash.
                        Pasásela al proveedor por un canal distinto al de la URL.
                    </Alert>
                    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: "'Roboto Mono', monospace", fontSize: 12, wordBreak: 'break-all', flex: 1 }}>
                            {keyMostrada?.api_key}
                        </Typography>
                        <Tooltip title="Copiar">
                            <IconButton size="small" onClick={copiarKey}><CopyIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setKeyMostrada(null)}>Ya la guardé</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}